import { Link, useLocation } from "react-router-dom";
import { Routes } from "../../../common/Routes";
// @ts-ignore
import ItgcProcessFlow from "../images/itgc-process-flow.png";
// @ts-ignore
import NonItgcProcessFlow from "../images/non-itgc-process-flow.png";

export function useProcessDocumentController() {
  const location = useLocation<{ processDocumentActiveTab?: string }>();

  const supportLink = (
    <a
      href="mailto:neoauthz-support@thoughtworks.com"
      rel={"noopener noreferrer"}
    >
      neoauthz-support@thoughtworks.com
    </a>
  );

  const businessTeamsMailLink = (text?: String) => (
    <a
      href="mailto:businessteams@thoughtworks.com"
      rel={"noopener noreferrer"}
    >
      {text || "businessteams@thoughtworks.com"}
    </a>
  );

  const businessTeamPlaybookLink = (
    <a
      href="https://docs.google.com/presentation/d/1mNjOTZaYBh5yVRsvqIUQjWZc97Y5F6jEdGOcV-rhDlM/"
      rel={"noopener noreferrer"}
      target="_blank"
    >
      Business Team Playbook
    </a>
  );

  const businessTeamsPageLink = (text: string) => (
    <Link
      to={`${Routes.UserGroups.basePath}/business-team`}
    >
      {text}
    </Link>
  );

  return {
    businessTeamCreationContent: {
      "Introduction": (
        <>
          <p>
            With Business teams we can group people based on their roles and responsibilities who need to
            have the same level of access to a set of information assets & systems to carry out business activities.
          </p>
          <p>
            Refer to the {businessTeamPlaybookLink} for more detailed reading about business teams.
          </p>
        </>
      ),
      "Create a business team": (
        <>
          <p>
            The business teams cannot be created in the AuthZ portal directly, rather it can be created by the AuthZ
            admin. If you are looking to create a new business team you can write to {supportLink}. Please make sure
            that the business team you look to create does not exist in the {businessTeamsPageLink("NEO AuthZ portal")}.
          </p>
          <p>
            The business team will be created when requested by a business team owner post validation that the business
            team is the
            appropriate one to be created and after necessary approvals. Allow us 2 business days to create a business
            team from the requested day.
          </p>
        </>
      ),
      "Business Team owner (BTO)": (
        <p>
          A business team owner is someone who is a business representative accountable to maintain the business team,
          the BTO can be someone part of the business function or a business lead of the team members who would be part
          of. There can be multiple BTOs for a business team. The business team owner(s) will be accountable to create,
          add members, maintain the business team so as to ensure that the members of the BT will get correct access to
          information and applications in thoughtworks. The necessity to constantly maintain the business team is vastly
          reduced when the members are added by condition and their attributes are updated as when changes happen.
          However, it is highly recommended that the BTO periodically audit the business team for its members and make
          necessary changes. The BTOs when added to a BT is not necessarily a member unless they get added to the
          business team as a member either through condition or by name. Only the members of the business team would
          get access to the information/systems desired to and not the BTO unless they are a member.
        </p>
      ),
      "Add members using membership condition": (
        <p>
          The Business Team Owner (BTO) can add members to the business team as a condition by defining a single or a
          combination of thoughtworker attributes that will pull one or more people that satisfy the condition. Adding
          members by condition is deterministic in nature and highly recommended vs the later. The condition will keep
          members up-to date based on the changes happening to their attributes. Someone who will newly fulfill the
          condition is automatically added, an existing member no longer satisfying the condition is removed
          automatically, thereby their access to information and systems also change automatically. The BTO and the
          members will be notified upon addition and removal of members to a BT.
        </p>
      ),
      "Add members by name": (
        <p>
          Adding members by name is to be done individually and those members will remain in the business team unless
          removed specifically. Addition of members by name is to be treated as an exemption and to be performed when
          any individual is to be on a business team but do not satisfy the condition applicable. However, at any point
          if the individually added member satisfies the condition and gets added to the team automatically, this
          addition made individually has to be removed to ensure the person leaves automatically upon the next change to
          their profile.
        </p>
      ),
      "Exclude members from a membership condition": (
        <p>
          1. Business team owners can exclude members listed from a membership condition so as to avoid select
          members getting added to a business team.<br />
          2. Members already added to a business team through a membership condition can also be excluded from a
          business team.<br />
          3. The excluded members will be highlighted and they would not have access to resources like other members
          of the business team.<br />
          4. Only members added through a condition can be excluded and cannot exclude member(s) added by
          name/employee ID. It is advised to remove them from the Business Team and not exclude them.<br />
          5. A member once excluded will remain excluded within the same business team even if they are added through
          other membership conditions.<br />
          6. Excluded members can be added back (included) to the team if required.<br />
        </p>
      ),
      "Publish a business team": (
        <>
          <p>
            A business team when created will be in DRAFT state by default, the Business Team Owner can form the team,
            add members, exclude members added by a condition etc.. and ask for the Business Team to be published. The
            idea is to form the business team fully, become comfortable in setting up a business team and make it live.
          </p>
          <p>
            Write to {supportLink} to publish a business team, once published a business team can
            NEVER return to draft status.
          </p>
        </>
      ),
      "Update default scope to a business team": (
        <>
          <p>
            The Business team's default scope is added by the AuthZ admin. If you are looking to add/update a new scope
            to a business team you can write to {supportLink} . Please make sure that the default
            scope you look to add/update for the business team, that business team should already exist in the NEO AuthZ
            portal. Also currently default scope accepts only these values: Global, Country and Account.
          </p>
          <p>
            The scope will be used as a default access scope for the business team members unless another business logic
            provides a different scope of access. If a user is part of multiple business teams, the highest of scopes
            among them would apply.
          </p>
        </>
      ),
      "Business Team Review Process": (
        <>
          <p>
            The Business Team Owners are expected to review the teams periodically to ensure it has the right set of
            members expected. The idea is to make sure we give the right people access to the right data and the
            business team’s membership is driven through deterministic conditions and avoid adding anyone by name/ID.
            The BTO themselves can initiate the review either or based on the drive organized by the gits AuthZ
            product team.
          </p>
          <p>
            The following are the criteria to review a business team:<br />
            <ul>
              <li>Review and verify all the members of your team are the expected ones.</li>
              <li>Review and verify already created membership conditions if they continue to hold good.</li>
              <li>
                If there are individual inclusion members added to your BT, explore ways to define deterministic
                conditions using user attributes that add the individual inclusion members through a rule.
              </li>
              <li>
                If the individual inclusion members and excluded members in your business team need an update to their
                profile like job title change, department change etc… Please reach out to the concerned team and drive
                the change.
              </li>
              <li>If there are excluded members in your BT, validate if they should continue to remain excluded.</li>
            </ul>
          </p>
          <p>
            The review should not take more than 30 minutes. Once the review is completed BTO can just write back to
            the {businessTeamsMailLink("AuthZ team")} confirming the review is complete (Let AuthZ team know if BTOs
            are delegating this effort to someone else in their team, AuthZ team could support this on a need basis).
          </p>
          <p>
            If BTO has any questions about your BT review, please do not hesitate to reach out
            to {businessTeamsMailLink()} for more information.
          </p>
        </>
      ),
    },
    accessReviewProcessForItgc: {
      "Introduction": (
        <p>
          AuthZ is a semi-automated <b>tool used to perform access reviews</b> for several software applications that
          we use in thoughtworks. AuthZ provides a self-service capability for application owners and Global access
          approvers to review who has what level of access privileges to the systems they own. A review is performed
          periodically for all the TWs applications to verify that only appropriate users have privileged access to
          applications. This review process also meets SoX compliance-related reporting requirements and is
          <b> accepted</b> as evidence by our <b>external auditors</b>. Clear and presentable evidence of access reviews
          are generated through the AuthZ access review process.
        </p>
      ),
      "ITGC Application Review": (
        <>
          <h4>a. Process Flow</h4>
          <p>
            <img src={ItgcProcessFlow} alt={"Process flow"} />
            <br /><br />
            Additionally, beyond the standard access review process, we are required to follow up with the application
            teams during two key stages:
            <ul style={{marginTop: "14px"}}>
              <li><b>Initial Prerequisite Stage</b> – to ensure timely file uploads.</li>
              <li><b>Post-Review Stage</b> – to facilitate final reconciliation after the review is closed.</li>
            </ul>
            We must also follow up with <b>access reviewers</b> to ensure they complete their reviews promptly, enabling
            us to
            close reviews for all applications on time. Once the reviewers have completed their tasks, we need to
            coordinate with the <b>Global Access Approver</b> to finalize and officially close the review process.
          </p>
          <h4>b. Prerequisites</h4>
          <p>
            <b>Request for User Access Data</b> - Access reviews for ITGC applications occur quarterly, however Gits
            Product Team have the ability to initiate reviews outside these cycles. Authz will promptly notify review
            schedules ahead to the start of cycle, requesting the Gits Product Team to upload the latest user access
            data and supporting evidence. The access owner condition/mapping file is optional, the system would consider
            the previous upload if absent.
          </p>
          <p>
            <b>File Uploading</b> - Gits Product Team can view the list of the Apps they are managing. ITGC
            applications are identified separately. The team can view the details of the next review cycle for the
            applications. They can upload mandatory User access dump (CSV), Screenshot (JPG/PNG) of the extraction and
            Access Owner condition (optional) file. The Access owner condition uploads are enabled at all times.
          </p>
          <p>
            <b>File Download</b> - The Gits Product Teams, Global Access Approver, Access Owners, Authz product team,
            and Internal Auditor are allowed to download the recently uploaded files for respective applications
            according to their preferences. The AuthZ product team will download the files to perform validations and
            enable the start of the review process.
          </p>
          <p>
            <b>File Verification</b> - After file uploads, the Authz team will perform validations, either accept or
            reject them based on results. If discrepancies arise, the Authz product team will provide reasons for
            rejection, prompting the Gits Product Team to re-upload the corrected files. Upon successful verification
            Global Access Approver can commence the review activity.
          </p>
          <p>
            <b>File expiration</b> - The Global Access Approver (GAA) must initiate the review within 7 days of
            successful verification of files; otherwise, the file upload expires. This will lead to prompting the
            Gits Product Teams to re-upload the latest files to begin the process all over again.
          </p>
          <h4>c. ITGC Access Review</h4>
          <b>Step 1 - Start Review</b>
          <p>
            The Global Access Approver can Initiate the access review within 7 days upon
            successful verification of files. Upon the initiation of the access review As a result, the latest
            validated files by Authz are linked to the review and a new review record is created and access reviewers
            are notified about it. The AuthZ product team can cancel reviews upon request for invalid data before
            closure, prompting the Global Access Approver to initiate a new review for the process to proceed as
            scheduled.
          </p>
          <b>Step 2 - Reviewing the Access</b>
          <p>
            Access Owners (AO) can begin to review assigned users' accesses,
            assigning one of the review decisions with appropriate reasons and comments when applicable. AO can save
            decisions by ensuring thorough consideration of the user and the respective user privilege, upon certainty
            submitting the decisions. When submitted the review decisions cannot be modified and create support
            (zendesk) tickets for the Gits Product Team when the decisions are actionable. A real time report of the
            access reviews and the decisions can be extracted at any point during and after the review.
          </p>
          <b>Step 3 - Review Closure</b>
          <p>
            Global Access Approver (GAA) is allowed to close the review after Access
            Owners (AOs) submit decisions for all users and when all support tickets are actioned (access remediation is
            completed) and closed. Alternatively the GAA can mark the user access data without decisions as
            "Not Reviewed" to close the review that is not complete yet.
          </p>
          <b>Step 4 - Reconciliation after Review Closure</b>
          <p>
            Upon closing the review, the Gits Product Team is
            asked to upload the latest user access dump with extraction screenshots. Authz reconciles the latest dump
            based on the review decisions affixed to users. The reconciliation will ensure the latest user access dump
            does reflect the expected access remediation performed in the target systems accurately.
          </p>
        </>
      ),
    },
    accessReviewProcessForNonItgc: {
      "Introduction": (
        <p>
          AuthZ is a semi-automated tool used to perform access reviews for several software applications that we use in
          thoughtworks. AuthZ provides a self-service capability for application owners and Global access approvers to
          review who has what level of access privileges to the systems they own. A review is performed periodically for
          all the TWs applications to verify that only appropriate users have privileged access to applications.
        </p>
      ),
      "Non-ITGC Application Review": (
        <>
          <h4>a. Process Flow</h4>
          <p>
            <img src={NonItgcProcessFlow} alt={"Process flow"} />
          </p>
          <h4>b. Prerequisites</h4>
          <p>
            <b>Request for User Access Data</b> - Global Access Approver request for files related to the Access review
            and the Gits Product Team will receive an email requesting the upload of the latest user accesses and
            access owner condition files. The access owner condition/mapping file is optional, the system would consider
            the previous upload if absent.
          </p>
          <p>
            <b>File Uploading</b> - Gits Product Team can view the list of the Apps they are managing. Non-ITGC
            applications are identified separately. The team can view the details of the next review for the
            applications. They can upload a mandatory User access dump (CSV) and Access Owner condition (optional) file.
            The Access owner condition uploads are enabled at all times.
          </p>
          <p>
            <b>File Download</b> -The Gits Product Teams, Global Access Approver, Access Owners, Authz product team,
            and Internal Auditor are allowed to download the recently uploaded files for respective applications
            according to their preferences.
          </p>
          <p>
            <b>File expiration</b> - The Global Access Approver (GAA) must initiate the review within 7 days of
            successful files upload; otherwise, the file upload expires. This will lead to prompting the Gits Product
            Teams to re-upload the latest files to begin the process all over again.
          </p>
          <h4>c. Access Review</h4>
          <b>Step 1 - Start Review</b>
          <p>
            The Global Access Approver can Initiate the access review within 7 days upon
            successful upload of files. Upon the initiation of the access review As a result, the latest uploaded files
            are linked to the review and a new review record is created and access reviewers are notified about it.
            <br />The AuthZ product team can cancel reviews upon request for invalid data before closure, prompting the
            Global Access Approver to initiate a new review for the process to proceed as scheduled.
          </p>
          <b>Step 2 - Reviewing the Access</b>
          <p>
            Access Owners (AO) can begin to review assigned users' accesses,
            assigning one of the review decisions with appropriate reasons and comments when applicable. AO can save
            decisions by ensuring thorough consideration of the user and the respective user privilege, upon certainty
            submitting the decisions. When submitted the review decisions cannot be modified and decisions are forwarded
            to the Gits Product Team for the next steps.
            <br />A real time report of the access reviews and the decisions can be extracted at any point during and
            after the review.
          </p>
          <b>Step 3 - Review Closure</b>
          <p>
            Global Access Approver (GAA) is allowed to close the review after Access
            Owners (AOs) submit decisions for all users. Alternatively the GAA can mark the user access data without
            decisions as "Not Reviewed" to close the review that is not complete yet.
          </p>
        </>
      ),
    },
    defaultActiveKey: location.state?.processDocumentActiveTab,
  };
}
